import React, { type ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonIcon,
  Icon,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';

import {
  CF_NEU_BLD,
  CF_NEU_STL,
  TABS_FILTERS,
  VIOLATIONS_LIST_SIDEPANEL_TABS,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type ViolationsTabSidePanelRowContextState } from '../../data-layer/violationsTabTypes';
import ViolationsSidePanelHistory from '../ViolationsSidePanel/ViolationsSidePanelHistory';
import ViolationsSidePanelSummary from '../ViolationsSidePanel/ViolationsSidePanelSummary';
import ViolationsSidePanelUser from '../ViolationsSidePanel/ViolationsSidePanelUser';

interface ViolationsSidePanelBodyProps {
  currentRowContext: ViolationsTabSidePanelRowContextState;
  sidepanelRef: React.RefObject<HTMLDivElement>;
  handleSidePanelBack: () => void;
  handleSidePanelClose: () => void;
}

/**
 * ViolationsGraphSidePanelBody
 */
function ViolationsGraphSidePanelBody({
  sidepanelRef,
  currentRowContext,
  handleSidePanelBack,
  handleSidePanelClose,
}: ViolationsSidePanelBodyProps): ReactElement {
  const l = useInvertedLocalize();
  const {
    controls: { tabInAction },
  } = useTypedSelector((state) => state.aag.violationsTab);

  const [activeTab, setActiveTab] = useState(
    VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_SUMMARY
  );

  useEffect(() => {
    setActiveTab(VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_SUMMARY);
  }, [currentRowContext.id]);

  const isMitigatedTab = useMemo(
    () => Boolean(tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value),
    [tabInAction]
  );

  const handleChange = (value: string) => setActiveTab(value);

  const violationByText = l('Violation by {username}', {
    username: currentRowContext.username,
  });

  return (
    <Box tag='div' className='Sod-ViolationsGraphSidePanelBody-wrapper'>
      <Box tag='div' className='Sod-ViolationsGraphSidePanelBody-headerWrapper'>
        <ButtonIcon
          kind='ghost'
          size='medium'
          icon={<Icon kind='ChevronLeft' />}
          onClick={() => handleSidePanelBack()}
          ariaLabel={l('Back to Risk SodInsights List')}
        />
        <Box className='Sod-ViolationsGraphSidePanelBody-titleBox'>
          <Typography kind='h3' color={CF_NEU_BLD} lineLimit={2}>
            {currentRowContext.title}
          </Typography>
          <Typography kind='body3' color={CF_NEU_STL} lineLimit={1}>
            {violationByText}
          </Typography>
        </Box>
        <ButtonIcon
          kind='ghost'
          size='medium'
          icon={<Icon kind='Close' />}
          onClick={() => handleSidePanelClose()}
          ariaLabel={l('Close Risk SodInsights Aside')}
          className='Sod-ViolationsGraphSidePanelBody-closeButton'
        />
      </Box>
      <TabGroup
        moreContainerRef={sidepanelRef}
        value={activeTab}
        size='medium'
        onChange={handleChange}>
        <TabItem
          label={l('Summary')}
          value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_SUMMARY}
          icon={<Icon kind='Document' size='medium' />}
        />
        <TabItem
          label={l('History')}
          value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_HISTORY}
          icon={<Icon kind='Time' size='medium' />}
        />
        <TabItem
          label={l('User')}
          value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_USER}
          icon={<Icon kind='User' size='medium' />}
        />
      </TabGroup>
      <ViolationsSidePanelSummary
        currentRowContext={currentRowContext}
        activeTab={activeTab}
        handleChange={handleChange}
        isMitigatedTab={isMitigatedTab}
      />
      <ViolationsSidePanelHistory
        currentRowContext={currentRowContext}
        activeTab={activeTab}
      />
      <ViolationsSidePanelUser
        currentRowContext={currentRowContext}
        activeTab={activeTab}
      />
    </Box>
  );
}

export default ViolationsGraphSidePanelBody;
